import { motion } from "framer-motion";
import { Moon, Star, BookOpen, Heart, HandHeart } from "lucide-react";
import { laylatulQadrContent } from "@/data/dailyDuas";
import { BackButton } from "./ui/BackButton";

interface LaylatulQadrGuideProps {
  onClose: () => void;
}

export const LaylatulQadrGuide = ({ onClose }: LaylatulQadrGuideProps) => {
  const content = laylatulQadrContent;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f0f23] text-cream">
      {/* Header */}
      <header className="sticky top-0 bg-[#1a1a2e]/95 backdrop-blur-sm z-10 border-b border-gold/20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <BackButton onClick={onClose} label="Back to Days" srLabel="Return to daily duas" variant="light" />
          <div className="flex items-center gap-2">
            <Moon className="w-4 h-4 text-gold" />
            <span className="text-xs uppercase tracking-[0.2em] text-gold">
              Laylatul Qadr
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 space-y-16">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-6"
        >
          <div className="flex justify-center gap-2">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className="w-3 h-3 text-gold"
                fill="currentColor"
              />
            ))}
          </div>
          <p className="text-gold text-xs uppercase tracking-[0.3em]">
            The Night of Power
          </p>
          <h1 className="font-arabic text-4xl md:text-5xl text-cream leading-relaxed" dir="rtl">
            لَيْلَةُ الْقَدْرِ خَيْرٌ مِّنْ أَلْفِ شَهْرٍ
          </h1>
          <p className="italic text-cream/80">
            "The Night of Decree is better than a thousand months"
          </p>
          <p className="text-xs text-cream/60">— Surah Al-Qadr, 97:3</p>
        </motion.section>

        {/* Overview */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
            <BookOpen className="w-4 h-4" />
            Understanding This Blessed Night
          </h2>
          <div className="space-y-4 text-cream/85 leading-relaxed">
            {content.overview.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </motion.section>

        {/* Signs */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="space-y-6"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
            <Star className="w-4 h-4" />
            Signs of Laylatul Qadr
          </h2>
          <div className="bg-[#0f0f23]/50 rounded-sm p-6 border border-gold/10">
            <ul className="space-y-3">
              {content.signs.map((sign, index) => (
                <li key={index} className="flex items-start gap-3 text-cream/80">
                  <span className="text-gold text-xs mt-1">✦</span>
                  <span>{sign}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* The Special Dua */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="space-y-6"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
            <Heart className="w-4 h-4" />
            The Dua of Laylatul Qadr
          </h2>
          
          <div className="bg-gradient-to-br from-gold/10 to-transparent rounded-sm p-8 border border-gold/20 space-y-6">
            <p className="font-arabic text-2xl md:text-3xl text-cream text-center leading-loose" dir="rtl">
              {content.specialDua.arabic}
            </p>
            <p className="text-center italic text-gold/80">
              {content.specialDua.transliteration}
            </p>
            <p className="text-center text-cream text-lg">
              "{content.specialDua.translation}"
            </p>
            <p className="text-center text-xs text-cream/60">
              — {content.specialDua.source}
            </p>
            <div className="mt-6 pt-6 border-t border-gold/10">
              <p className="text-cream/70 text-sm leading-relaxed">
                {content.specialDua.explanation}
              </p>
            </div>
          </div>
        </motion.section>

        {/* Recommended Acts */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="space-y-6"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
            <HandHeart className="w-4 h-4" />
            Recommended Acts of Worship
          </h2>
          
          <div className="space-y-4">
            {content.recommendedActs.map((act, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.05 }}
                className="bg-[#0f0f23]/50 rounded-sm p-5 border border-gold/10 space-y-2"
              >
                <h3 className="text-cream font-medium">{act.title}</h3>
                <p className="text-cream/70 text-sm">{act.description}</p>
                {act.arabic && (
                  <div className="pt-3 mt-3 border-t border-gold/10">
                    <p className="font-arabic text-sm text-gold/80" dir="rtl">
                      {act.arabic}
                    </p>
                    {act.transliteration && (
                      <p className="text-xs text-cream/50 italic mt-1">
                        {act.transliteration}
                      </p>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Nightly Focus for Odd Nights */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="space-y-6"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
            <Moon className="w-4 h-4" />
            Focus for the Odd Nights
          </h2>
          
          <div className="space-y-4">
            {Object.entries(content.nightlyFocus).map(([night, focus], index) => (
              <motion.div
                key={night}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 + index * 0.05 }}
                className="bg-gradient-to-r from-gold/5 to-transparent rounded-sm p-5 border-l-2 border-gold/50"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-gold/20 text-gold px-3 py-1 rounded-full text-xs font-medium">
                    Night {night}
                  </span>
                  <span className="text-cream font-medium">{focus.focus}</span>
                </div>
                <p className="text-cream/70 text-sm mb-2">{focus.specialDua}</p>
                <p className="text-gold/60 text-xs italic">{focus.reminder}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Closing */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center py-8 space-y-4"
        >
          <div className="flex justify-center gap-2">
            {[...Array(3)].map((_, i) => (
              <Star
                key={i}
                className="w-2 h-2 text-gold"
                fill="currentColor"
              />
            ))}
          </div>
          <p className="text-cream/60 text-sm max-w-md mx-auto">
            May Allah grant you Laylatul Qadr and accept all your worship this Ramadan. Ameen.
          </p>
          <button
            onClick={onClose}
            className="mt-6 px-6 py-3 border border-gold/50 text-gold text-sm uppercase tracking-wider hover:bg-gold/10 transition-colors rounded-sm"
          >
            Return to Daily Duas
          </button>
        </motion.section>
      </main>
    </div>
  );
};
